import type { User } from '../types';
import { CURRENT_SITE } from '../site-config';

/**
 * Utility functions for per-site (white-label) access control.
 *
 * Roles say what a user may do; these say which sites they may do it on.
 */

/**
 * Every site a user belongs to: the append-only `sites` history UNION the
 * current `site_plans` keys.
 *
 * Both halves are needed. An admin-granted site lands in site_plans first, and
 * a record predating `sites` may have neither.
 */
export const userSiteIds = (user: User | null): string[] => {
  if (!user) return [];
  const sitePlans = user.subscription_details?.site_plans || {};
  return Array.from(new Set([...(user.sites || []), ...Object.keys(sitePlans)]));
};

/** Whether the user registered on (subscribed to, or was granted) a given site. */
export const hasSiteAccess = (user: User | null, siteId: string): boolean => {
  if (!user || !siteId) return false;
  return userSiteIds(user).includes(siteId);
};

/** Whether the user registered on the site this build serves. */
export const isCurrentSiteMember = (user: User | null): boolean =>
  hasSiteAccess(user, CURRENT_SITE.siteId);
